import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, KeyRound, CheckCircle, Sparkles, Layers, GitMerge, ShieldCheck, ArrowRight, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG_IMAGE = 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80';

const features = [
  { icon: Sparkles, title: 'AI Classification', desc: 'Multi-level L1-L4 spend categorization' },
  { icon: Layers, title: 'Data Cleansing', desc: 'Deduplication & description splitting' },
  { icon: GitMerge, title: 'Supplier Normalization', desc: 'Merge rules & name standardization' },
  { icon: ShieldCheck, title: 'Spend Analytics', desc: 'Dashboards, trends & cost visibility' },
];

const stats = [
  { value: 'L1-L4', label: 'Classification Depth' },
  { value: '99.4%', label: 'Processing Rate' },
  { value: '516', label: 'Records Processed' },
];

const LeftPanel = () => (
  <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${BG_IMAGE})` }}
    />
    <div className="absolute inset-0 bg-[#0a1628]/85" />

    <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
      <div className="shrink-0">
        <h2 className="text-4xl xl:text-5xl font-extrabold tracking-tight">
          <span className="text-white">Data</span><span className="text-[#22d3ee]">Refinery</span>
        </h2>
        <p className="text-[#94a3b8] text-xs tracking-[0.25em] uppercase font-medium mt-2">AI-Powered Data Cleansing and Categorization</p>
      </div>

      <div className="flex-1 flex flex-col justify-center py-10">
        <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
          Transform raw spend into{' '}
          <span className="text-[#22d3ee]">actionable intelligence</span>
        </h1>
        <p className="text-[#94a3b8] text-sm leading-relaxed max-w-md mb-10">
          Ingest, cleanse, classify, and enrich your procurement data with AI — turning messy spreadsheets into structured spend insights.
        </p>

        <div className="grid grid-cols-2 gap-3 max-w-md mb-10">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-white/[0.06] backdrop-blur-md border border-white/[0.08] rounded-lg p-4 transition-colors hover:bg-white/[0.1]"
            >
              <f.icon size={20} className="text-[#22d3ee] mb-2.5" />
              <p className="text-white text-sm font-semibold mb-0.5">{f.title}</p>
              <p className="text-[#64748b] text-xs">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-8 max-w-md">
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-[#22d3ee] text-xl font-bold">{s.value}</p>
              <p className="text-[#64748b] text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[#475569] text-xs">&copy; {new Date().getFullYear()} DataRefinery. All rights reserved.</p>
    </div>
  </div>
);

import { CookieSettingsLink } from '@/components/CookieConsent';

const RightPanel = ({ children }) => (
  <div className="w-full lg:w-1/2 flex flex-col min-h-screen bg-white">
    <div className="lg:hidden p-6 pb-0">
      <h2 className="text-2xl font-extrabold tracking-tight">
        <span className="text-[#0f172a]">Data</span><span className="text-[#0891b2]">Refinery</span>
      </h2>
    </div>

    <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-12 lg:px-16 xl:px-24">
      <div className="w-full max-w-[420px]">
        {children}
      </div>
    </div>

    <div className="px-6 pb-6 text-center space-y-2">
      <p className="text-[#94a3b8] text-xs">Trusted by procurement teams worldwide</p>
      <CookieSettingsLink />
    </div>
  </div>
);

const AuthLayout = ({ children }) => (
  <div className="flex min-h-screen">
    <LeftPanel />
    <RightPanel>{children}</RightPanel>
  </div>
);

export const Login = ({ onSuccess, onForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const finishLogin = (_token, user) => {
    // Backend sets the httpOnly `dr_access_token` cookie during login, so
    // there's no need to persist the JWT in localStorage. We still cache
    // `user` for an instant hydrated render on the next page load — the
    // cookie remains the sole source of truth for authentication.
    localStorage.setItem('user', JSON.stringify(user));
    toast.success('Login successful!');
    onSuccess(user);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.mfa_required) {
        setMfaChallenge(response.data.mfa_challenge);
        toast.info('Enter your authenticator code');
        return;
      }
      if (response.data.mfa_setup_required && !response.data.user?.mfa_enabled) {
        toast.info('Your role requires MFA — set it up in Privacy & Data to secure your account.', { duration: 8000 });
      }
      finishLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (mfaCode.length !== 6) {
      toast.error('Enter your 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/verify`, {
        mfa_challenge: mfaChallenge, code: mfaCode,
      });
      finishLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    setMfaChallenge(null);
    setMfaCode('');
  };

  return (
    <AuthLayout>
      <div>
        {mfaChallenge ? (
          <>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-1" data-testid="login-mfa-heading">Two-factor authentication</h1>
            <p className="text-[#64748b] text-sm mb-8">
              Enter the 6-digit code from your authenticator app.
            </p>
            <form onSubmit={handleMfaSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#475569] uppercase tracking-wide">Authenticator code</Label>
                <Input
                  data-testid="login-mfa-input"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123 456"
                  className="h-14 text-2xl font-mono tracking-[0.4em] text-center bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={cancelMfa} data-testid="login-mfa-cancel">
                  Back
                </Button>
                <Button type="submit" disabled={loading || mfaCode.length !== 6}
                  className="flex-1 h-11 bg-[#0f172a] hover:bg-[#1e293b] text-white font-semibold rounded-md"
                  data-testid="login-mfa-submit">
                  {loading ? 'Verifying…' : 'Verify & sign in'}
                </Button>
              </div>
            </form>
          </>
        ) : (
        <>
        <h1 className="text-2xl font-bold text-[#0f172a] mb-1" data-testid="login-heading">Welcome back</h1>
        <p className="text-[#64748b] text-sm mb-8">
          Sign in to continue to <span className="font-semibold text-[#0f172a]">DataRefinery</span>
        </p>

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-medium text-[#475569] uppercase tracking-wide">
              Email Address
            </Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs font-medium text-[#475569] uppercase tracking-wide">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                data-testid="login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md pr-10"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium h-11 rounded-md flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? 'Signing in...' : (
              <>
                Sign In
                <ArrowRight size={16} />
              </>
            )}
          </Button>
        </form>

        <div className="mt-5 text-center">
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={onForgotPassword}
            className="text-sm text-[#64748b] hover:text-[#0f172a] transition-colors"
          >
            Forgot your password?
          </button>
        </div>
        </>
        )}
      </div>
    </AuthLayout>
  );
};

export const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('If an account exists, a reset link has been emailed.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div>
        <button
          onClick={onBack}
          data-testid="back-to-login"
          className="flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#0f172a] mb-8 transition-colors"
        >
          <ArrowLeft size={14} /> Back to login
        </button>

        {!sent ? (
          <>
            <div className="w-10 h-10 rounded-full bg-[#eff6ff] flex items-center justify-center mb-5">
              <Mail size={18} className="text-[#2563eb]" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-1">Forgot password</h1>
            <p className="text-[#64748b] text-sm mb-8">Enter your email and we'll send you a secure reset link.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-xs font-medium text-[#475569] uppercase tracking-wide">
                  Email Address
                </Label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md"
                />
              </div>
              <Button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={loading}
                className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium h-11 rounded-md transition-colors"
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="forgot-sent-panel">
            <div className="w-12 h-12 rounded-full bg-[#f0fdf4] flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={22} className="text-[#16a34a]" />
            </div>
            <h2 className="text-xl font-bold text-[#0f172a] mb-2">Check your email</h2>
            <p className="text-sm text-[#64748b] mb-4 leading-relaxed">
              If an account exists for <span className="font-medium text-[#0f172a]">{email}</span>, a password reset
              link has been sent. The link is valid for 30 minutes.
            </p>
            <p className="text-xs text-[#94a3b8]">
              Don't see it? Check your spam folder or try again in a minute.
            </p>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export const ResetPassword = ({ token, onBack, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const missingToken = !token || !token.trim();

  // Mirror backend policy so users get instant feedback instead of a 400.
  const validatePassword = (pw) => {
    if (pw.length < 10) return 'Password must be at least 10 characters';
    if (!/[A-Z]/.test(pw)) return 'Password must include an uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Password must include a lowercase letter';
    if (!/\d/.test(pw)) return 'Password must include a digit';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    const err = validatePassword(newPassword);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      toast.success('Password reset successfully! Please sign in with your new password.');
      setDone(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div>
        <button
          onClick={onBack}
          data-testid="back-to-login-from-reset"
          className="flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#0f172a] mb-8 transition-colors"
        >
          <ArrowLeft size={14} /> Back to login
        </button>

        {missingToken ? (
          <div className="text-center py-4" data-testid="reset-missing-token">
            <div className="w-12 h-12 rounded-full bg-[#fef2f2] flex items-center justify-center mx-auto mb-4">
              <KeyRound size={22} className="text-[#dc2626]" />
            </div>
            <h2 className="text-xl font-bold text-[#0f172a] mb-2">Reset link is missing or invalid</h2>
            <p className="text-sm text-[#64748b] mb-5 leading-relaxed">
              This page needs a reset token supplied in the URL. Please use the link from the reset email
              you received. If it doesn't work, request a new one.
            </p>
            <Button
              onClick={onBack}
              data-testid="reset-missing-back-btn"
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium"
            >
              Back to login
            </Button>
          </div>
        ) : !done ? (
          <>
            <div className="w-10 h-10 rounded-full bg-[#fffbeb] flex items-center justify-center mb-5">
              <KeyRound size={18} className="text-[#d97706]" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-1">Set new password</h1>
            <p className="text-[#64748b] text-sm mb-2">Choose a strong password for your account</p>
            <p className="text-[#94a3b8] text-xs mb-8">At least 10 characters, with upper, lower, and a digit.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="new-pass" className="text-xs font-medium text-[#475569] uppercase tracking-wide">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="new-pass"
                    data-testid="reset-new-password-input"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••••"
                    required
                    className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                  >
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pass" className="text-xs font-medium text-[#475569] uppercase tracking-wide">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirm-pass"
                    data-testid="reset-confirm-password-input"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••"
                    required
                    className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="reset-submit-button"
                disabled={loading}
                className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium h-11 rounded-md transition-colors"
              >
                {loading ? 'Resetting…' : 'Reset Password'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="reset-done-panel">
            <div className="w-12 h-12 rounded-full bg-[#f0fdf4] flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={22} className="text-[#16a34a]" />
            </div>
            <h2 className="text-xl font-bold text-[#0f172a] mb-2">Password Reset Complete</h2>
            <p className="text-sm text-[#64748b] mb-5">
              All existing sessions have been signed out for your security. Please sign in with your new password.
            </p>
            <Button
              onClick={onSuccess}
              data-testid="go-to-login-button"
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium"
            >
              Go to Login
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export const Register = ({ onSuccess, onSwitchToLogin }) => {
  return null;
};
